// NavItem.js
import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography, useMediaQuery } from '@mui/material';

// project imports
import { MENU_OPEN, SET_MENU } from 'store/actions';

// assets
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// ==============================|| SIDEBAR MENU LIST ITEMS ||============================== //

const NavItem = ({ item, level, setOpen }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const customization = useSelector((state) => state.customization);
    const matchesSM = useMediaQuery(theme.breakpoints.down('lg'));
    const leftDrawerOpened = useSelector((state) => state.customization.opened);
    const Icon = item.icon;
    
    // This checks if the current URL matches this item's URL
    const isActive = () => {
        // Handle root/default path specially
        if (item.url === '/' || item.url === '') {
            return document.location.pathname === '/' || document.location.pathname === '';
        }
        
        // For other paths, ensure it's not a partial match of a different route
        // For example, '/dashboard' shouldn't match '/dashboard-analytics'
        const currentPath = document.location.pathname;
        if (item.url && currentPath.startsWith(item.url)) {
            // Check if it's an exact match or a child route
            return currentPath === item.url || 
                   currentPath.startsWith(item.url + '/') || 
                   item.url === currentPath.substring(0, item.url.length);
        }
        return false;
    };
    
    const active = isActive();
    
    const itemIcon = item?.icon ? (
        <Icon stroke={1.5} size="24px" style={{ color: "#ffffff" }} />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: active ? 8 : 6,
                height: active ? 8 : 6,
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    let itemTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    let listItemProps = {
        component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />)
    };
    if (item?.external) {
        listItemProps = { component: 'a', href: item.url, target: itemTarget };
    }

    const itemHandler = () => {
        // Only dispatch this item's ID, which will clear previous selections
        dispatch({ type: MENU_OPEN, id: item.id });
        
        if (matchesSM) dispatch({ type: SET_MENU, opened: false });
        // if (setOpen) setOpen(false);
    };

    // Update active state on page load and URL changes
    useEffect(() => {
        if (active) {
            dispatch({ type: MENU_OPEN, id: item.id });
        }
    }, [dispatch, active, item.id]);
    
    return (
        <Tooltip title={item.tooltip || ''} placement="right" disableHoverListener={!item.tooltip} arrow>
            <ListItemButton
                {...listItemProps}
                disabled={item.disabled}
                sx={{
                    borderRadius: `${customization.borderRadius}px`,
                    mb: 0.5,
                    display: "flex",
                    gap: 1,
                    alignItems: 'center',
                    backgroundColor: active ? 'rgba(255, 255, 255, 0.08) !important' : 'inherit',
                    py: level > 1 ? 1 : 1.25,
                    // ml: level == 2 ? 3 : 0,
                }}
                selected={active}
                onClick={itemHandler}
                >
                {/* {console.log(level,item.title,"lelelee")} */}
                <ListItemIcon sx={{
                    my: 'auto', 
                    minWidth: !item?.icon ? 18 : 10, 
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                    {itemIcon}
                </ListItemIcon>
                <ListItemText
                    primary={
                        leftDrawerOpened ? (
                            <Typography
                                variant={active ? 'h5' : 'body1'}
                                color="#fff"
                            >
                                {item.title}
                            </Typography>
                        ) : !["Dashboard", "Bank", "Documents"].includes(item?.title) ? (
                            <Typography
                                variant={active ? 'h5' : 'body1'}
                                color="#fff"
                            >
                                {item.title}
                            </Typography>
                        ) : (
                            <></>
                        )
                    }
                    secondary={
                        item.caption && (
                            <Typography 
                                variant="caption" 
                                sx={{ ...theme.typography.subMenuCaption }} 
                                display="block" 
                                gutterBottom
                            >
                                {item.caption}
                            </Typography>
                        )
                    }
                />
                {item.chip && (
                    <Chip
                        color={item.chip.color}
                        variant={item.chip.variant}
                        size={item.chip.size}
                        label={item.chip.label}
                        avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
                    />
                )}
            </ListItemButton>
        </Tooltip>
    );
};

NavItem.propTypes = {
    item: PropTypes.object,
    level: PropTypes.number,
    setOpen: PropTypes.func
};

export default NavItem;